import store from "../redux/store";
import { baseApi } from "../redux/baseAPI";
import { toast } from "react-toastify";
import { isLoggedInUser, isPatientChatMessage } from "../helpers/utility";

let sessionExpiredDebounceTimer = null;

const baseApiHelper = () => {

  const authData = JSON.parse(localStorage.getItem('auth'));

  const isLoggedInUserInMentions = (mentions, loggedInUserId) => {
    if (!mentions || loggedInUserId == null) return false;

    if (!Array.isArray(mentions)) return false;
    return mentions.some((m) => String(m?.id) === String(loggedInUserId));
  };

  const getStoredChatFilter = () => {
    try {
      return JSON.parse(localStorage.getItem("chatFilter") || "{}") || {};
    } catch {
      return {};
    }
  };

  const hasAttachment = (data) => {
    const media = data?.message?.media || data?.media || [];
    return Array.isArray(media) && media.length > 0;
  };

  const matchesActiveChatFilters = (data, unreadMentionCount = 0) => {
    const filters = getStoredChatFilter();
    const hasActiveFilters = filters.unread_only || filters.urgent_only || filters.unacknowledged || filters.mention || filters.attachment;

    if (!hasActiveFilters) {
      return true;
    }

    const unreadMessageCount = data?.unread_message_count || (data?.isMsgFromDifferentThread ? 1 : 0);
    const unreadUrgentCount = data?.unread_urgent_count || (data?.urgent && data?.isMsgFromDifferentThread ? 1 : 0);
    const mentionedLoggedInUser = isLoggedInUserInMentions(data?.mentions, authData?.id);

    if (filters.unread_only && !unreadMessageCount) {
      return false;
    }
    if (filters.urgent_only && !(data?.urgent || unreadUrgentCount)) {
      return false;
    }
    if (filters.unacknowledged && !(data?.urgent && !data?.acknowledged_at)) {
      return false;
    }
    if (filters.mention && !(mentionedLoggedInUser || unreadMentionCount > 0 || data?.unread_mention_count > 0)) {
      return false;
    }
    if (filters.attachment && !hasAttachment(data)) {
      return false;
    }

    return true;
  };

  const getSelectedLocationIds = (locations) => String(locations || '')
    .split(',')
    .map((locationId) => locationId.trim())
    .filter(Boolean);

  const getChatLocationId = (chat) => chat?.location_id || chat?.location?.id;

  const matchesSelectedLocations = (chat, locations) => {
    const selectedLocationIds = getSelectedLocationIds(locations);
    if (!selectedLocationIds.length) {
      return true;
    }

    const chatLocationId = getChatLocationId(chat);
    return chatLocationId !== undefined && chatLocationId !== null && selectedLocationIds.includes(String(chatLocationId));
  };

  const parseMessagePayload = (message) => {
    if (!message || typeof message !== 'string') {
      return message;
    }
    try {
      return JSON.parse(message);
    } catch (error) {
      return { text: message };
    }
  };

  const getRecentMessageFields = (data) => {
    const message = parseMessagePayload(data?.message ?? data?.last_message);
    const messageTime = data?.last_message_at ?? data?.created;
    return {
      ...(message ? { message, last_message: JSON.stringify(message) } : {}),
      ...(messageTime ? { created: messageTime, last_message_at: messageTime } : {}),
    };
  };

  const hasNewMessage = (data) => {
    return !!(data?.message || data?.last_message || data?.created || data?.last_message_at);
  };

  const shouldSuppressUnreadCount = (data) => {
    return ['appointment', 'request_appointment'].includes(data?.message?.event_type);
  };

  const preserveUnreadCounts = (mergedChat, existingChat, incomingData) => {
    ['unread_message_count', 'unread_urgent_count', 'unread_mention_count'].forEach((countKey) => {
      if (incomingData?.[countKey] === undefined || incomingData?.[countKey] === null) {
        mergedChat[countKey] = existingChat?.[countKey];
      }
    });
    return mergedChat;
  };

  const updatePatientChatCacheThroughPusher = (data, locations) => {
    
    if(!isPatientChatMessage(data)){
      return;
    }
    
    store.dispatch(baseApi.util.updateQueryData('getRecentChats', {location_id: locations}, (recentsChats) => {
      
      if(data.type == "patient_location_changed"){
        const foundPatientChatIndex = recentsChats.data.findIndex((item)=> item.pid == data.pid && item.location.id == data.old_location);
        if(foundPatientChatIndex > -1){
          // remove the chat thread if the location is not found in the practice locations
          recentsChats.data.splice(foundPatientChatIndex, 1);
        }
      }
      else{
        const foundChatIndex = recentsChats.data.findIndex((item)=> item.pid == data.pid);
        const locationMatchedData = matchesSelectedLocations(data, locations)
          ? data
          : foundChatIndex > -1 && !getChatLocationId(data) && matchesSelectedLocations(recentsChats.data[foundChatIndex], locations)
            ? { ...data, location_id: getChatLocationId(recentsChats.data[foundChatIndex]) }
            : null;

        if(!locationMatchedData){
          if(foundChatIndex > -1){
            recentsChats.data.splice(foundChatIndex, 1);
          }
          return;
        }

        data = locationMatchedData;
        if(isLoggedInUser(authData, data) && foundChatIndex > -1){
          if(!data.isChatThreadClicked && hasNewMessage(data)){
            chatThreadonTop(recentsChats.data, foundChatIndex, data);
          } else {
            recentsChats.data[foundChatIndex] = mergePatientChatPreservingTags(recentsChats.data[foundChatIndex], data);
          }
        }
        else if(!isLoggedInUser(authData, data)){
          if(foundChatIndex > -1){
            if(data.isMsgFromDifferentThread && !shouldSuppressUnreadCount(data)){
              data.unread_urgent_count = data.urgent ? (recentsChats.data[foundChatIndex].unread_urgent_count || 0) + 1 : recentsChats.data[foundChatIndex].unread_urgent_count || 0;
              const mentionedLoggedInUser = isLoggedInUserInMentions(data.mentions, authData?.id);
              data.unread_mention_count = mentionedLoggedInUser ? (recentsChats.data[foundChatIndex].unread_mention_count || 0) + 1 : recentsChats.data[foundChatIndex].unread_mention_count || 0;
              data.unread_message_count = (recentsChats.data[foundChatIndex].unread_message_count || 0) + 1;
              delete data.isMsgFromDifferentThread;
              chatThreadonTop(recentsChats.data, foundChatIndex, data);
            }
            else{
              if(!data.isChatThreadClicked && hasNewMessage(data)){
                chatThreadonTop(recentsChats.data, foundChatIndex, data);
              } else {
                recentsChats.data[foundChatIndex] = mergePatientChatPreservingTags(recentsChats.data[foundChatIndex], data);
              }
            }
          }
          else{
            let unread_message_count = 0;
            let unread_urgent_count = 0;
            let unread_mention_count = 0;
            if(data.isMsgFromDifferentThread && !shouldSuppressUnreadCount(data)){
              delete data.isMsgFromDifferentThread;
              unread_message_count = 1;
              const mentionedLoggedInUser = isLoggedInUserInMentions(data.mentions, authData?.id);
              if(data.urgent){
                unread_urgent_count = 1;
              }
              if(mentionedLoggedInUser){
                unread_mention_count = 1;
              }
            }
            const newChatThread = {
              ...data,
              ...getRecentMessageFields(data),
              unread_message_count,
              unread_urgent_count,
              unread_mention_count,
            };
            if (matchesActiveChatFilters(newChatThread)) {
              recentsChats.data.unshift(newChatThread);
            }
          }
        }
      }
      dedupePatientChatThreads(recentsChats.data);
    }));
    
    // Invalidate unread count when new messages are received
    if(!shouldSuppressUnreadCount(data)){
      store.dispatch(baseApi.util.invalidateTags(["UnreadMessageCount"]));
    }
  }

  const updateLocationChatCacheThroughPusher = (data, locations) => {

    if(isPatientChatMessage(data)){
      return;
    }

    store.dispatch(baseApi.util.updateQueryData('getLocationChats', {location_id: locations}, (recentsChats) => {

      const dataLocationId = getChatLocationId(data);
      const foundChatIndex = recentsChats.data.findIndex((item)=> getChatLocationId(item) == dataLocationId);
      if(!matchesSelectedLocations(data, locations)){
        if(foundChatIndex > -1){
          recentsChats.data.splice(foundChatIndex, 1);
        }
        return;
      }

      if(foundChatIndex > -1){
        data.location = recentsChats.data[foundChatIndex].location;
      }
      
      if(isLoggedInUser(authData, data) && foundChatIndex > -1){
        if(data.isChatThreadClicked || !data.isMsgFromDifferentThread || shouldSuppressUnreadCount(data)){
          if(!data.isChatThreadClicked && hasNewMessage(data)){
            chatThreadonTop(recentsChats.data, foundChatIndex, data);
          } else {
            recentsChats.data[foundChatIndex] = preserveUnreadCounts(
              {...recentsChats.data[foundChatIndex], ...data, ...getRecentMessageFields(data)},
              recentsChats.data[foundChatIndex],
              data
            );
          }
        }
        else if(!data.isChatThreadClicked){ // Don't move chat to top on chat click
          data.unread_message_count = (recentsChats.data[foundChatIndex].unread_message_count || 0) + 1;
          chatThreadonTop(recentsChats.data, foundChatIndex, data);
        }
      }
      else if(!isLoggedInUser(authData, data)){
        if(foundChatIndex > -1){
          if(data.isMsgFromDifferentThread && !shouldSuppressUnreadCount(data)){
            data.unread_message_count = (recentsChats.data[foundChatIndex].unread_message_count || 0) + 1;
            data.unread_urgent_count = data.urgent ? (recentsChats.data[foundChatIndex].unread_urgent_count || 0) + 1 : recentsChats.data[foundChatIndex].unread_urgent_count || 0;
            const mentionedLoggedInUser = isLoggedInUserInMentions(data.mentions, authData?.id);
            data.unread_mention_count = mentionedLoggedInUser ? (recentsChats.data[foundChatIndex].unread_mention_count || 0) + 1 : recentsChats.data[foundChatIndex].unread_mention_count || 0;
            delete data.isMsgFromDifferentThread;
            chatThreadonTop(recentsChats.data, foundChatIndex, data);
          }
          else{
              if(!data.isChatThreadClicked && hasNewMessage(data)){
                chatThreadonTop(recentsChats.data, foundChatIndex, data);
              } else {
                recentsChats.data[foundChatIndex] = preserveUnreadCounts(
                  {...recentsChats.data[foundChatIndex], ...data, ...getRecentMessageFields(data)},
                  recentsChats.data[foundChatIndex],
                  data
                );
              }
          }
        }
        else{
          let unread_message_count = 0;
          let unread_urgent_count = 0;
          let unread_mention_count = 0;
          if(data.isMsgFromDifferentThread && !shouldSuppressUnreadCount(data)){
            delete data.isMsgFromDifferentThread;
            unread_message_count = 1;
            unread_urgent_count = data.urgent ? 1 : 0;
            unread_mention_count = isLoggedInUserInMentions(data.mentions, authData?.id) ? 1 : 0;
          }
          const newChatThread = {
            ...data,
            ...getRecentMessageFields(data),
            unread_message_count,
            unread_urgent_count,
            unread_mention_count,
          };
          if (matchesActiveChatFilters(newChatThread)) {
            recentsChats.data.unshift(newChatThread);
          }
        }
      }
    }));
    
    // Invalidate unread count when new messages are received
    if(!shouldSuppressUnreadCount(data)){
      store.dispatch(baseApi.util.invalidateTags(["UnreadMessageCount"]));
    }
  }

  // const updatePatientChatsCacheThroughPusher = (data) => {
  //   const { pid, user, user_type, nurse_id, user_id, message_id } = data;
  //   const { id: auth_id } = authData;
  
  //   store.dispatch(baseApi.util.updateQueryData('getPatientChats', { pid }, (patientChats) => {
  //     try {
  //         const foundMsgIndex = patientChats.data.findIndex((msg) => `${msg.id}` === `${message_id}`);
  //         if (foundMsgIndex > -1) {
  //           patientChats.data[foundMsgIndex] = {
  //             ...patientChats.data[foundMsgIndex],
  //             ...data,
  //             acknowledged_by_user_id: data.user,
  //           };
  //           const parsedData = JSON.parse(JSON.stringify(patientChats.data));
  //         } else {
  //           console.warn(`Message with id ${msg_id} not found in patient chats.`);
  //         }
  //     } catch (error) {
  //       console.error('Error updating patientChats data:', error);
  //     }
  //   }));
  // }

  const mergeDefinedFields = (existingValue = {}, incomingValue = {}) => {
    const mergedValue = { ...existingValue };
    Object.entries(incomingValue || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        mergedValue[key] = value;
      }
    });
    return Object.keys(mergedValue).length ? mergedValue : incomingValue;
  };

  const mergePatientChatPreservingTags = (existingChat, incomingData) => {
    const merged = preserveUnreadCounts(
      { ...existingChat, ...incomingData, ...getRecentMessageFields(incomingData) },
      existingChat,
      incomingData
    );
    merged.patient = mergeDefinedFields(existingChat.patient, incomingData.patient);
    merged.location = mergeDefinedFields(existingChat.location, incomingData.location);
    const hasExistingTags = existingChat.patient?.patient_group_color != null &&
      (Array.isArray(existingChat.patient.patient_group_color) ? existingChat.patient.patient_group_color.length > 0 : true);
    const incomingHasNoTags = incomingData.patient?.patient_group_color == null ||
      (Array.isArray(incomingData.patient?.patient_group_color) && incomingData.patient.patient_group_color.length === 0);
    if (merged.patient && hasExistingTags && incomingHasNoTags) {
      merged.patient = { ...merged.patient, patient_group_color: existingChat.patient.patient_group_color };
    }
    return merged;
  };
  
  const chatThreadonTop = (recentsChats, index, data) => {
    // bring the chat thread on top if msg received from different user
    const updatedMsg = mergePatientChatPreservingTags(recentsChats[index], data);
    recentsChats.splice(index, 1);
    recentsChats.unshift(updatedMsg);
  }

  const dedupePatientChatThreads = (recentsChats) => {
    const seenChatKeys = new Set();
    for (let index = 0; index < recentsChats.length; index += 1) {
      const chat = recentsChats[index];
      const chatKeyValue = chat?.pid ?? chat?.patient_id ?? chat?.patient?.id ?? chat?.patient?.patient_id ?? chat?.patient?.mrn ?? chat?.chat_id ?? chat?.id;
      const chatKey = chatKeyValue === undefined || chatKeyValue === null || chatKeyValue === '' ? null : String(chatKeyValue);
      if (!chatKey) {
        continue;
      }
      if (seenChatKeys.has(chatKey)) {
        recentsChats.splice(index, 1);
        index -= 1;
        continue;
      }
      seenChatKeys.add(chatKey);
    }
  }

  const updateChatMessageCacheThroughPusher = (data) => {
    store.dispatch(baseApi.util.updateQueryData('getPatientChats', {pid: data.pid, limit: 50, page: 1}, (patientChats) => {
      patientChats.data.unshift(data);
    }))
  }

  const invalidateTags = (tagsToInvalidate) => {
    if(tagsToInvalidate && tagsToInvalidate.length > 0) {
      store.dispatch(baseApi.util.invalidateTags(tagsToInvalidate));
    }
  }

  const invalidateThroughSocket = (receivedComponent, extraData, updateLocalData) => {
    store.dispatch((dispatch, getState) => {
      switch (receivedComponent) {
        case 'labs':
          invalidateTags(['Result', 'labOrder', 'LabOrdersInNote', 'LabPendingOrders', 'LabAwaitingResults', 'LabFutureOrders']);
          localDocumateRTKStateOverwrite(updateLocalData, ['DocumateLabsList']);
          if (extraData) {
            /// do something with your extraData, if sent
          }
        break;
      }
    });
  }

  const localDocumateRTKStateOverwrite = (dataToOverwrite, rtkQueryMethods) => {
    invalidateTags(rtkQueryMethods);
  }

  const responseErrorCheck = (response) => {
    if ((response?.code === -1 || response?.code === '-1') && response?.msg?.toLowerCase()?.includes("session has expired")) {
      if (sessionExpiredDebounceTimer) {
        clearTimeout(sessionExpiredDebounceTimer);
      }
      sessionExpiredDebounceTimer = setTimeout(() => {
        toast.error(response?.msg);
      }, 1500);
    }
    else if ((response?.code === -1 || response?.code === '-1') && response?.msg && response?.msg?.length > 0) {
      toast.error(response?.msg);
    } else if (typeof response?.code == 'undefined') {
      toast.error("Something went wrong, please try again later.");
    }
  }

  return {
    updatePatientChatCacheThroughPusher: updatePatientChatCacheThroughPusher,
    updateLocationChatCacheThroughPusher: updateLocationChatCacheThroughPusher,
    updateChatMessageCacheThroughPusher: updateChatMessageCacheThroughPusher,
    invalidateTags: invalidateTags,
    invalidateThroughSocket: invalidateThroughSocket,
    localDocumateRTKStateOverwrite: localDocumateRTKStateOverwrite,
    responseErrorCheck: responseErrorCheck,
    // updatePatientChatsCacheThroughPusher: updatePatientChatsCacheThroughPusher
  };
}

export default baseApiHelper;